import React, { useState, useEffect } from 'react';
import SectionHeader from './SectionHeader';
import './OpenSourceProfile.css';
import { FaGithub, FaCodeBranch, FaCode, FaCheckCircle, FaExternalLinkAlt } from 'react-icons/fa';

const GITHUB_TOKEN = process.env.REACT_APP_GITHUB_TOKEN;

// Helper function to make authenticated GitHub API requests
const fetchGitHubAPI = async (url) => {
    const headers = {
        'Accept': 'application/vnd.github.v3+json',
    };

    // Add authorization header with Bearer token if available
    if (GITHUB_TOKEN) {
        headers['Authorization'] = `Bearer ${GITHUB_TOKEN}`;
    }

    const response = await fetch(url, { headers });

    if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
            throw new Error('GitHub API authentication failed. Please check your token.');
        }
        if (response.status === 429) {
            throw new Error('GitHub API rate limit exceeded. Please wait or use a token.');
        }
        throw new Error(`GitHub API error: ${response.status} ${response.statusText}`);
    }

    return response;
};

function OpenSourceProfile() {
    const [stats, setStats] = useState({
        repos: 0,
        openPRs: 0,
        mergedPRs: 0,
        loading: true
    });

    const [contributions, setContributions] = useState([]);

    useEffect(() => {
        const fetchGitHubData = async () => {
            try {
                const username = 'tanmayrajurkar';

                // 1. Fetch User Data (Repos) - Use authenticated endpoint if token available
                const userEndpoint = GITHUB_TOKEN
                    ? `https://api.github.com/user`
                    : `https://api.github.com/users/${username}`;
                const userRes = await fetchGitHubAPI(userEndpoint);
                const userData = await userRes.json();

                // 2. Search API for PRs
                const fetchSearchCount = async (query) => {
                    const res = await fetchGitHubAPI(`https://api.github.com/search/issues?q=${query}`);
                    const data = await res.json();
                    return data.total_count || 0;
                };

                const [openCount, mergedCount] = await Promise.all([
                    fetchSearchCount(`type:pr+author:${username}+state:open`),
                    fetchSearchCount(`type:pr+author:${username}+is:merged`)
                ]);

                // 3. Fetch Contributed Projects (Open & Merged)
                const [openContribs, mergedContribs] = await Promise.all([
                    fetchGitHubAPI(`https://api.github.com/search/issues?q=type:pr+author:${username}+state:open&sort=updated&per_page=20`).then(res => res.json()),
                    fetchGitHubAPI(`https://api.github.com/search/issues?q=type:pr+author:${username}+is:merged&sort=updated&per_page=20`).then(res => res.json())
                ]);

                // Combine and sort by update time desc
                const allItems = [...(openContribs.items || []), ...(mergedContribs.items || [])];
                allItems.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

                // Extract unique repositories with all contribution info
                const repoMap = new Map();

                allItems.forEach(item => {
                    const repoUrl = item.repository_url;
                    const name = repoUrl.replace('https://api.github.com/repos/', '');

                    // Filter out own repositories (case insensitive)
                    if (name.toLowerCase().startsWith(`${username.toLowerCase()}/`)) {
                        return;
                    }

                    // Filter out airvitap/airvitap.github.io
                    if (name.toLowerCase() === 'airvitap/airvitap.github.io') {
                        return;
                    }
                    // Filter out airvitap/gsoc
                    if (name.toLowerCase() === 'airvitap/gsoc') {
                        return;
                    }
                    

                    // Store all PRs for each repo
                    if (!repoMap.has(name)) {
                        repoMap.set(name, {
                            name: name,
                            url: item.html_url.split('/pull/')[0],
                            apiUrl: repoUrl,
                            contributions: [{
                                prTitle: item.title,
                                prState: item.state === 'open' ? 'open' : 'merged',
                                prUrl: item.html_url,
                                updatedAt: item.updated_at
                            }],
                            updatedAt: item.updated_at
                        });
                    } else {
                        // Add PR to existing repo's contributions list
                        const existing = repoMap.get(name);
                        existing.contributions.push({
                            prTitle: item.title,
                            prState: item.state === 'open' ? 'open' : 'merged',
                            prUrl: item.html_url,
                            updatedAt: item.updated_at
                        });
                        // Update repo's most recent update time
                        if (new Date(item.updated_at) > new Date(existing.updatedAt)) {
                            existing.updatedAt = item.updated_at;
                        }
                    }
                });

                // Sort repos by most recent contribution, then sort PRs within each repo
                const uniqueRepos = Array.from(repoMap.values())
                    .map(repo => ({
                        ...repo,
                        contributions: repo.contributions.sort((a, b) =>
                            new Date(b.updatedAt) - new Date(a.updatedAt)
                        )
                    }))
                    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

                setStats({
                    repos: userData.public_repos || 0,
                    openPRs: openCount,
                    mergedPRs: mergedCount,
                    loading: false
                });

                setContributions(uniqueRepos.slice(0, 5)); // Top 5 contributed repos

            } catch (error) {
                console.error("Error fetching GitHub data:", error);
                setStats(prev => ({ ...prev, loading: false }));
            }
        };

        fetchGitHubData();
    }, []);

    const StatCard = ({ icon, count, label }) => (
        <div className="oss-stat-card">
            <div className="oss-icon">{icon}</div>
            <div className="oss-number">
                {stats.loading ? '-' : count}
            </div>
            <div className="oss-label">{label}</div>
        </div>
    );

    return (
        <div className="oss-container">
                <SectionHeader index="06" label="Upstream" title="Open source" />
                <p className="oss-intro">
                    Work merged into other people's codebases, the strongest code review there is.
                </p>

                <div className="oss-grid-advanced">
                    <StatCard icon={<FaCodeBranch />} count={stats.repos} label="Repositories" />
                    <StatCard icon={<FaCode />} count={stats.openPRs} label="Open PRs" />
                    <StatCard icon={<FaCheckCircle />} count={stats.mergedPRs} label="Merged PRs" />
                </div>

                {/* Contributed Projects List */}
                <div className="oss-contributions">
                    <h3 className="mono-label oss-subhead">Recent contributions · pull requests upstream</h3>
                    {stats.loading ? (
                        <p>Loading projects...</p>
                    ) : (
                        <div className="contributions-list">
                            {contributions.map((repo, index) => (
                                <div key={index} className="contribution-item">
                                    <a href={`https://github.com/${repo.name}`} target="_blank" rel="noopener noreferrer" className="contribution-repo">
                                        <span className="repo-icon"><FaGithub /></span>
                                        <span className="repo-name">{repo.name}</span>
                                        <span className="repo-arrow"><FaExternalLinkAlt /></span>
                                    </a>
                                    {repo.contributions && repo.contributions.length > 0 && (
                                        <div className="contribution-info">
                                            {repo.contributions.map((contrib, contribIndex) => (
                                                <a
                                                    key={contribIndex}
                                                    href={contrib.prUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="contribution-pr"
                                                >
                                                    <span className={`pr-state ${contrib.prState}`}>
                                                        {contrib.prState === 'merged' ? <FaCheckCircle /> : <FaCodeBranch />}
                                                        {contrib.prState === 'merged' ? 'Merged' : 'Open'}
                                                    </span>
                                                    <span className="pr-title">{contrib.prTitle}</span>
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                            {contributions.length === 0 && <p className="no-contributions">No recent merged PRs found.</p>}
                        </div>
                    )}
                </div>

                <div className="oss-cta">
                    <a href="https://github.com/tanmayrajurkar" target="_blank" rel="noopener noreferrer" className="btn-doc">
                        <FaGithub /> Full history on GitHub
                    </a>
                </div>
        </div>
    );
}

export default OpenSourceProfile;
